import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, race, take, takeUntil } from 'rxjs';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import { ValidationRulesApiActions } from '../../state/actions';
import {
  IRuleOperation,
  IRuleParamDefinition,
  IRuleTypeDefinition,
  WidgetType,
} from '../../models/validation-rule';

const WIDGET_TYPES: WidgetType[] = [
  'TEXT',
  'NUMBER',
  'BOOLEAN',
  'SELECT',
  'MULTI_SELECT',
  'TAGS',
  'REGEX_TEXTAREA',
];

/**
 * Modal for creating/editing a ``RuleTypeDefinition`` and managing its
 * nested ``RuleParamDefinition`` rows.
 *
 * Modes:
 * - ``definition === null`` => create. The form requires ``code``,
 *   ``operation``, and ``label``. After save the modal stays open with
 *   the newly-saved definition selected so the admin can immediately add
 *   params.
 * - ``definition !== null`` => edit. Top form patches label, description,
 *   is_active, sort_order. Param list below is independently CRUDable
 *   (each row PATCHes individually).
 *
 * ``code`` and ``kind`` are immutable on edit. To change them, the admin
 * creates a new rule type definition and migrates rules over by string.
 */
@Component({
  selector: 'app-rule-type-definition-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './rule-type-definition-modal.component.html',
})
export class RuleTypeDefinitionModalComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() modalId!: string;
  @Input() definition: IRuleTypeDefinition | null = null;
  @Output() closed = new EventEmitter<void>();
  /**
   * Emitted after a successful CREATE, with the newly-saved definition.
   * The parent should bind this to a handler that promotes the modal to
   * edit mode for the new def (sets the `[definition]` @Input) so the
   * admin can keep editing — most importantly, add params, which the
   * template only exposes once a definition exists.
   */
  @Output() created = new EventEmitter<IRuleTypeDefinition>();

  @ViewChild('dialog') dialogEl!: ElementRef<HTMLDialogElement>;

  private fb = inject(FormBuilder);
  private dialog = inject(DialogService);
  private facade = inject(ValidationRulesFacade);
  private actions$ = inject(Actions);
  /**
   * Fires when the modal is destroyed or the user closes it; cancels
   * any in-flight create-action listener so we don't auto-transition a
   * dismissed dialog into edit mode.
   */
  private cancelCreateListener$ = new Subject<void>();

  operations = this.facade.operationsSignal;
  readonly WIDGET_TYPES = WIDGET_TYPES;

  defForm!: FormGroup;
  paramForm!: FormGroup;
  editingParamId = signal<number | null>(null);

  /**
   * Reactive mirror of `defForm.operation` so the operation help-card
   * (description + accepted-key chips) recomputes when the admin picks
   * a different operation. ``computed`` against this signal handles the
   * lookup against ``operations()``.
   */
  selectedOperationId = signal<number | null>(null);

  /** The op the admin has currently picked, or null. */
  selectedOperation = computed<IRuleOperation | null>(() => {
    const opId = this.selectedOperationId();
    if (opId === null) return null;
    return this.operations().find((o) => o.id === opId) ?? null;
  });

  /**
   * Param keys the picked op accepts but the admin hasn't yet defined a
   * ``RuleParamDefinition`` for. Surfaced as a hint chip set so the
   * editor knows what's still available to add. Edits don't change the
   * op's accepted-keys list — those come from the Python decorator.
   */
  unaddedParamKeys = computed<string[]>(() => {
    const op = this.selectedOperation();
    if (!op) return [];
    const defined = new Set((this.definition?.param_defs ?? []).map((p) => p.key));
    return op.allowed_param_keys.filter((k) => !defined.has(k));
  });

  /**
   * Keys offered by the Key ng-select in the param add/edit form.
   *
   * - Add mode (no ``editingParamId``): ``unaddedParamKeys()`` only — so
   *   the admin can't accidentally pick a key already defined here.
   * - Edit mode: union of the param's current key plus ``unaddedParamKeys``.
   *   This keeps the dropdown coherent (the existing key is selected) while
   *   still preventing a rename onto another already-defined key.
   *
   * The ng-select also has ``[addTag]="true"`` so a key the op-registry
   * doesn't yet know about can still be typed as a custom tag — handy
   * for forward-compat with backend changes in flight.
   */
  paramKeyOptions = computed<string[]>(() => {
    const unadded = this.unaddedParamKeys();
    const editingId = this.editingParamId();
    if (editingId === null) return unadded;
    const editingKey = this.definition?.param_defs.find((p) => p.id === editingId)?.key;
    if (editingKey && !unadded.includes(editingKey)) return [editingKey, ...unadded];
    return unadded;
  });

  /**
   * Reactive mirror of the param form's ``key`` field. Used to drive
   * the per-key Widget restriction + Choices auto-population.
   */
  paramKeyValueSignal = signal<string>('');

  /**
   * Widget types offered for the current key, intersected with the
   * operation's per-key ``param_spec[key].widgets`` allowlist. If the
   * spec doesn't constrain this key, the full {@link WIDGET_TYPES} list
   * is returned. In edit mode the current widget is always included so
   * the ng-select has a valid selected item even when the spec changed
   * after the param was originally created.
   */
  availableWidgetTypes = computed<WidgetType[]>(() => {
    const op = this.selectedOperation();
    const key = this.paramKeyValueSignal();
    const allowed = op?.param_spec?.[key]?.widgets;
    if (!allowed || allowed.length === 0) return [...WIDGET_TYPES];
    // Preserve the currently-selected widget if it's outside the
    // constraint (e.g. legacy data, or admin manually overrode).
    const current = this.paramForm?.value?.widget_type as WidgetType | undefined;
    if (current && !allowed.includes(current)) return [current, ...allowed];
    return [...allowed];
  });

  /**
   * Inline default values for the Choices field when the current key
   * has a static ``values`` list in ``param_spec``. Read by the param
   * form's auto-populate effect below.
   */
  paramKeyDefaultValues = computed<string[] | null>(() => {
    const op = this.selectedOperation();
    const key = this.paramKeyValueSignal();
    return op?.param_spec?.[key]?.values ?? null;
  });

  ngAfterViewInit(): void {
    if (this.modalId && this.dialogEl?.nativeElement) {
      this.dialog.register(this.modalId, this.dialogEl.nativeElement);
    }
    this.initForms();
  }

  ngOnDestroy(): void {
    if (this.modalId) this.dialog.unregister(this.modalId);
    this.cancelCreateListener$.next();
    this.cancelCreateListener$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['definition'] && this.defForm) this.initForms();
  }

  private initForms(): void {
    const d = this.definition;
    this.defForm = this.fb.group({
      code: [{ value: d?.code ?? '', disabled: !!d }, Validators.required],
      operation: [d?.operation ?? null, Validators.required],
      label: [d?.label ?? '', Validators.required],
      description: [d?.description ?? ''],
      is_active: [d?.is_active ?? true],
      sort_order: [d?.sort_order ?? 0],
    });
    // Mirror operation FK into a signal so the help-card recomputes
    // when the admin swaps operations.
    this.selectedOperationId.set(this.defForm.value.operation ?? null);
    this.defForm
      .get('operation')
      ?.valueChanges.subscribe((v: number | null) =>
        this.selectedOperationId.set(v ?? null),
      );
    this.resetParamForm();
  }

  private resetParamForm(): void {
    this.paramForm = this.fb.group({
      key: ['', Validators.required],
      label: [''],
      help_text: [''],
      widget_type: ['TEXT', Validators.required],
      is_required: [false],
      default_value: [''],
      choices: [[] as string[]],
      sort_order: [0],
    });
    this.editingParamId.set(null);
    this.paramKeyValueSignal.set('');

    // Wire the per-key auto-config: whenever the admin picks (or types)
    // a different key, update the reactive mirror, then:
    //   1. If the new key has an op-defined widget allowlist AND the
    //      current widget is outside it, snap the widget to the first
    //      allowed widget (typical case: picking `target_model` should
    //      flip the Widget dropdown to SELECT immediately).
    //   2. If the new key has inline `values` in the spec AND the
    //      current Choices field is empty, pre-populate it so the
    //      admin doesn't have to retype an allowlist that already
    //      lives in code.
    // Subsequent edits are left alone; we don't fight the admin.
    this.paramForm.get('key')?.valueChanges.subscribe((k: string | null) => {
      const key = (k ?? '').trim();
      this.paramKeyValueSignal.set(key);
      const op = this.selectedOperation();
      const spec = op?.param_spec?.[key];
      if (!spec) return;
      if (spec.widgets && spec.widgets.length > 0) {
        const current = this.paramForm?.value?.widget_type as WidgetType | undefined;
        if (!current || !spec.widgets.includes(current)) {
          this.paramForm?.patchValue({ widget_type: spec.widgets[0] }, { emitEvent: false });
        }
      }
      if (spec.values && spec.values.length > 0) {
        const currentChoices = this.paramForm?.value?.choices as string[] | undefined;
        if (!currentChoices || currentChoices.length === 0) {
          this.paramForm?.patchValue(
            { choices: [...spec.values] },
            { emitEvent: false },
          );
        }
      }
    });
  }

  close(): void {
    // Cancel any pending create-success listener so a late-arriving
    // success doesn't reopen the modal after the user dismissed it.
    this.cancelCreateListener$.next();
    this.closed.emit();
  }

  saveDefinition(): void {
    if (this.defForm.invalid) {
      this.defForm.markAllAsTouched();
      return;
    }
    const raw = this.defForm.getRawValue();
    const payload = {
      code: raw.code,
      operation: Number(raw.operation),
      label: raw.label,
      description: raw.description ?? '',
      is_active: !!raw.is_active,
      sort_order: Number(raw.sort_order ?? 0),
    };
    if (this.definition) {
      // Code is immutable on edit; the disabled control isn't sent. The
      // backend would reject it anyway.
      this.facade.patchRuleTypeDefinition(this.definition.id, payload);
      this.close();
      return;
    }

    // CREATE path: don't close the modal. Listen for the success/error
    // action on the NgRx stream; on success, emit the new definition so
    // the parent can swap the modal into edit mode for it (which makes
    // the params editor section appear). The race+take(1) ensures we
    // only react to the next outcome, not future ones.
    this.facade.createRuleTypeDefinition(payload);
    race(
      this.actions$.pipe(
        ofType(ValidationRulesApiActions.createRuleTypeDefSuccess),
      ),
      this.actions$.pipe(
        ofType(ValidationRulesApiActions.createRuleTypeDefError),
      ),
    )
      .pipe(take(1), takeUntil(this.cancelCreateListener$))
      .subscribe((action) => {
        if (action.type === ValidationRulesApiActions.createRuleTypeDefSuccess.type) {
          this.created.emit(action.payload);
        }
        // On error the effect already showed a toast; keep the create
        // form open so the admin can correct and retry.
      });
  }

  beginEditParam(p: IRuleParamDefinition): void {
    this.editingParamId.set(p.id ?? null);
    this.paramForm.patchValue({
      key: p.key,
      label: p.label,
      help_text: p.help_text,
      widget_type: p.widget_type,
      is_required: p.is_required,
      default_value: p.default_value ?? '',
      choices: p.choices ?? [],
      sort_order: p.sort_order,
    });
  }

  cancelEditParam(): void {
    this.resetParamForm();
  }

  saveParam(): void {
    if (!this.definition) return;
    if (this.paramForm.invalid) {
      this.paramForm.markAllAsTouched();
      return;
    }
    const raw = this.paramForm.value;
    const payload = {
      rule_type: this.definition.id,
      key: raw.key,
      label: raw.label ?? '',
      help_text: raw.help_text ?? '',
      widget_type: raw.widget_type,
      is_required: !!raw.is_required,
      default_value: raw.default_value === '' ? null : raw.default_value,
      choices: Array.isArray(raw.choices) ? raw.choices : [],
      sort_order: Number(raw.sort_order ?? 0),
    };
    const editingId = this.editingParamId();
    if (editingId !== null) {
      this.facade.patchParamDefinition(editingId, payload);
    } else {
      this.facade.createParamDefinition(payload);
    }
    this.resetParamForm();
  }

  deleteParam(p: IRuleParamDefinition): void {
    if (p.id !== undefined) this.facade.deleteParamDefinition(p.id);
  }

  needsChoices(): boolean {
    const w = this.paramForm?.value?.widget_type as WidgetType | undefined;
    return w === 'SELECT' || w === 'MULTI_SELECT';
  }
}
